import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { EmployeeLink } from '@/common/components/shared/link/EmployeeLink';
import type { ListEmployeeAlertsQuery } from '@/common/graphql';
import { EmployeeEditTab } from '@/common/types';

type Reason = ListEmployeeAlertsQuery['listEmployeeAlertsByEmployee']['items'][number]['reasons'][number];

const tabNames: Record<Exclude<Reason['__typename'], undefined>, EmployeeEditTab> = {
  // general-information
  EmployeeAlertHiredDateBeforeAnyMindStarts: EmployeeEditTab.GENERAL_INFORMATION,
  EmployeeAlertInactiveManager: EmployeeEditTab.GENERAL_INFORMATION,
  EmployeeAlertInvalidJapanesePhoneticName: EmployeeEditTab.GENERAL_INFORMATION,
  EmployeeAlertInvalidLegalFullName: EmployeeEditTab.GENERAL_INFORMATION,
  EmployeeAlertInvalidPreferredFullName: EmployeeEditTab.GENERAL_INFORMATION,
  // personal-information
  EmployeeAlertEmptyDateOfBirth: EmployeeEditTab.PERSONAL_INFORMATION,
  EmployeeAlertAgeOutOfRange: EmployeeEditTab.PERSONAL_INFORMATION,
  // job-history
  EmployeeAlertContractsNotContinuous: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertContractsNotFound: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertFirstContractStartDateNotSameAsHiredDate: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertFirstJobHistoryStartDateNotSameAsHiredDate: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertInvalidEmployeeCode: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertJobHistoriesNotContinuous: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertJobHistoriesNotFound: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertLastContractEndDateNotSameAsResignationDate: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertLastContractEndDateNotSameAsLastJobHistoryEndDate: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertLastContractWillEnd: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertLastJobHistoryWillEnd: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertLastJobHistoryEndDateNotSameAsResignationDate: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertEmployeeNotInactiveButNoActiveContract: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertEmployeeNotInactiveButNoActiveJobHistory: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertDuplicateCompanyContracts: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertDuplicateMainContracts: EmployeeEditTab.JOB_HISTORY,
  EmployeeAlertDuplicateJobHistories: EmployeeEditTab.JOB_HISTORY,
  // off-boarding
  EmployeeAlertResignationDateBeforeAnyMindStarts: EmployeeEditTab.OFF_BOARDING,
  EmployeeAlertResignationDateBeforeHiredDate: EmployeeEditTab.OFF_BOARDING,
  EmployeeAlertResignationDateBeforeLastWorkingDate: EmployeeEditTab.OFF_BOARDING,
  EmployeeAlertLastWorkingDateBeforeAnyMindStarts: EmployeeEditTab.OFF_BOARDING,
  EmployeeAlertLastWorkingDateBeforeHiredDate: EmployeeEditTab.OFF_BOARDING,
  // the dates that clear this alert are registered on the Off-boarding tab
  EmployeeAlertProbationFailedStillEmployed: EmployeeEditTab.OFF_BOARDING,
};

interface Props {
  className?: string;
  employeeId: string;
  reason: Reason;
}

export const EmployeeAlertTypeWithLink = ({ className, employeeId, reason }: Props) => {
  const location = useLocation();

  const tab = useMemo(
    () => (reason.__typename ? tabNames[reason.__typename] : EmployeeEditTab.GENERAL_INFORMATION),
    [reason.__typename],
  );
  const isOpeningPage = useMemo(() => {
    if (location.pathname === `/employees/edit/${employeeId}/${tab}`) return true;
    if (tab === EmployeeEditTab.GENERAL_INFORMATION) return location.pathname === `/employees/edit/${employeeId}`;
    return false;
  }, [employeeId, location.pathname, tab]);

  if (isOpeningPage) {
    return <span className={className}>{reason.summary}</span>;
  }

  return (
    <EmployeeLink className={className} employeeId={employeeId} tab={tab}>
      {reason.summary}
    </EmployeeLink>
  );
};
